import { IonButton, IonContent, IonPage } from '@ionic/react';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { openBookingPage } from '../../lib/booking.js';

// SuspendedPage (T-266). Mostrata da AuthGate quando il claim JWT
// `account_block_reason === 'suspended'`. Sostituisce l'intera app (hard block
// totale): niente tab, niente accesso ai dati. Differisce dalla TrialExpiredPage
// perché qui l'account è stato sospeso (manualmente dalla dashboard o dal cron
// che ribalta i trial scaduti) e l'utente deve contattare l'assistenza per la
// riattivazione: due vie, WhatsApp oppure prenotazione di un appuntamento.
//
// L'owner di piattaforma non vede mai questa pagina: il JWT hook gli forza
// `account_block_reason=null` (trial_active=true) indipendentemente dallo stato
// del tenant attivo.
export function SuspendedPage(): JSX.Element {
  const { signOut, profile } = useAuth();

  // Messaggio WhatsApp pre-compilato col nome del profilo, quando disponibile.
  // Numero +39 348 638 2343 (lo stesso usato dal funnel su trackeat.eu).
  const greeting = profile?.full_name ? `Ciao, sono ${profile.full_name}.` : 'Ciao,';
  const waText = encodeURIComponent(
    `${greeting} Il mio account TrackEAT risulta sospeso e vorrei riattivarlo per tornare a usare l'app.`,
  );
  const waUrl = `https://wa.me/393486382343?text=${waText}`;

  async function handleSignOut(): Promise<void> {
    try {
      await signOut();
    } catch {
      // best-effort: anche se la chiamata fallisce, la sessione locale
      // verrà comunque ripulita al prossimo refresh.
    }
  }

  return (
    <IonPage>
      <IonContent
        style={{ '--background': 'linear-gradient(180deg, #fee2e2 0%, #fff1f2 100%)' } as React.CSSProperties}
      >
        <div
          className="te-auth-shell"
          style={{
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '32px 24px',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 32,
                background: 'linear-gradient(135deg, #f43f5e, #be123c)',
                color: '#fff',
                fontSize: 48,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 14px 32px rgba(190, 18, 60, 0.3)',
                marginBottom: 24,
              }}
            >
              🔒
            </div>
            <h1
              style={{
                margin: '0 0 12px',
                fontSize: 26,
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.01em',
              }}
            >
              Account sospeso
            </h1>
            <p style={{ margin: 0, fontSize: 15, color: '#475569', lineHeight: 1.55 }}>
              Il tuo account <strong>TrackEAT</strong> è momentaneamente sospeso,
              quindi l'app non è utilizzabile.
              <br />
              Nessun dato è andato perso: è tutto al sicuro nel database e torna
              disponibile appena riattiviamo l'account.
            </p>
          </div>

          <div
            style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: 18,
              marginBottom: 20,
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
              border: '1px solid #fecdd3',
            }}
          >
            <p style={{ margin: '0 0 4px', fontSize: 14, color: '#334155', lineHeight: 1.55, fontWeight: 600 }}>
              Per riattivare l'account contattaci:
            </p>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              scrivici su WhatsApp per una risposta veloce, oppure prenota un
              appuntamento e ne parliamo con calma.
            </p>
          </div>

          <IonButton
            expand="block"
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={
              {
                '--background': '#25d366',
                '--background-hover': '#1ebe5b',
                '--background-activated': '#1ebe5b',
                '--color': '#ffffff',
                '--border-radius': '14px',
                height: 56,
                fontSize: 16,
                fontWeight: 700,
                marginBottom: 12,
              } as React.CSSProperties
            }
          >
            Scrivici su WhatsApp
          </IonButton>

          <IonButton
            expand="block"
            onClick={() => void openBookingPage()}
            style={
              {
                '--background': '#0f172a',
                '--background-hover': '#1e293b',
                '--background-activated': '#1e293b',
                '--color': '#ffffff',
                '--border-radius': '14px',
                height: 54,
                fontSize: 15,
                fontWeight: 700,
                marginBottom: 12,
              } as React.CSSProperties
            }
          >
            Prenota un appuntamento
          </IonButton>

          <IonButton
            expand="block"
            fill="clear"
            onClick={handleSignOut}
            style={
              {
                '--color': '#64748b',
                '--border-radius': '14px',
                height: 48,
                fontSize: 14,
                fontWeight: 500,
              } as React.CSSProperties
            }
          >
            Esci e cambia account
          </IonButton>

          {profile?.email && (
            <p
              style={{
                margin: '20px 0 0',
                textAlign: 'center',
                fontSize: 12,
                color: '#94a3b8',
              }}
            >
              Account: {profile.email}
            </p>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
